import type {TenantBrandingPayload} from './tenantConfig'

export type PhoneCountry = {
  iso_code_2: string
  dial_code: string
  country: string
  country_ar?: string | null
  pattern: string
  prefix_pattern: string
  min_length: number
  max_length: number
  format: string
  example?: string
}

// Jordan is only the fail-open default for when no tenant-config was loaded (plain Dumyah, or a
// failed fetch) — the same fallback AdminPhoneValidation applies server-side. Every tenant's own
// country and every country's rule come from /api/v1/tenant-config (`phone_validation`).
const jordan: PhoneCountry = {
  iso_code_2: 'JO', dial_code: '962', country: 'Jordan', country_ar: 'الأردن',
  pattern: '^7[789]\\d{7}$', prefix_pattern: '^7(?:[789]|$)', min_length: 9, max_length: 9, format: '7[789]XXXXXXX',
}

// The server's rule for a country without a fixed national format.
const genericRule = {pattern: '^\\d{7,12}$', prefix_pattern: '^\\d', min_length: 7, max_length: 12, format: '7–12 digits'}

let configured: NonNullable<TenantBrandingPayload['phone_validation']> | null = null

export function setPhoneValidationConfig(value: TenantBrandingPayload['phone_validation']): void {
  configured = value || null
}

const digits = (value: unknown) => String(value ?? '').replace(/\D/g, '')

export function phoneCountries(): PhoneCountry[] {
  return configured?.countries?.length ? configured.countries as PhoneCountry[] : [jordan]
}

export function defaultPhoneCountry(): PhoneCountry {
  const iso = String(configured?.iso_code_2 || jordan.iso_code_2).toUpperCase()
  const listed = phoneCountries().find((country) => country.iso_code_2 === iso)
  if (listed) return listed
  if (!configured?.dial_code) return jordan
  return {iso_code_2: iso, dial_code: configured.dial_code, country: configured.country || iso, country_ar: configured.country_ar, ...genericRule}
}

export function phoneCountryForValue(value: unknown, storedInternational = false): PhoneCountry {
  const raw = String(value ?? '').trim()
  const number = digits(raw).replace(/^00/, '')
  const explicit = raw.startsWith('+') || digits(raw).startsWith('00')
  const fallback = defaultPhoneCountry()
  const local = number.replace(/^0+/, '')
  const matches = [...phoneCountries()].sort((a, b) => b.dial_code.length - a.dial_code.length)
  const canUseStoredPrefix = (country: PhoneCountry) => country.iso_code_2 === fallback.iso_code_2
    || country.min_length === country.max_length
    || number.length > fallback.max_length
  if (storedInternational && !explicit) {
    // A saved number without its "+": the tenant's own fixed format first, then a dial code it starts with.
    if (fallback.min_length === fallback.max_length && new RegExp(fallback.pattern).test(local)) return fallback

    const storedCountry = matches.find((country) => {
      if (!country.dial_code || country.dial_code.length === 1 || !number.startsWith(country.dial_code)) return false
      if (!canUseStoredPrefix(country)) return false
      const national = number.slice(country.dial_code.length).replace(/^0+/, '')
      return new RegExp(country.pattern).test(national)
    })
    if (storedCountry) return storedCountry

    const shortCountry = matches.find((country) => {
      if (country.dial_code.length !== 1 || !number.startsWith(country.dial_code)) return false
      if (!canUseStoredPrefix(country)) return false
      return new RegExp(country.pattern).test(number.slice(1).replace(/^0+/, ''))
    })
    if (shortCountry) return shortCountry
  }
  if (!explicit) return fallback

  return matches.find((country) => country.dial_code && number.startsWith(country.dial_code)) || fallback
}

export function nationalPhoneDigits(value: unknown, country: PhoneCountry, storedInternational = false): string {
  const raw = String(value ?? '').trim()
  let number = digits(raw)
  if (number.startsWith('00')) number = number.slice(2)
  if ((storedInternational || raw.startsWith('+') || digits(raw).startsWith('00') || number.length > country.max_length) && number.startsWith(country.dial_code)) {
    number = number.slice(country.dial_code.length)
  }
  return number
}

export function capPhoneDigits(value: unknown, country: PhoneCountry): string {
  const number = nationalPhoneDigits(value, country)
  return number.slice(0, country.max_length + (number.startsWith('0') ? 1 : 0))
}

export function formatPhone(value: unknown, country: PhoneCountry, storedInternational = false): string {
  const number = nationalPhoneDigits(value, country, storedInternational)
  if (!number) return ''
  if (country.iso_code_2 === 'JO') {
    return number.startsWith('0')
      ? [number.slice(0, 3), number.slice(3, 6), number.slice(6, 10)].filter(Boolean).join(' ')
      : [number.slice(0, 2), number.slice(2, 5), number.slice(5, 9)].filter(Boolean).join(' ')
  }
  return number.match(/.{1,3}/g)?.join(' ') || number
}

export function internationalPhone(value: unknown, country = phoneCountryForValue(value)): string {
  const national = capPhoneDigits(value, country).replace(/^0+/, '')
  return national ? `+${country.dial_code}${national}` : ''
}

export function phoneValidationMessage(value: unknown, country = phoneCountryForValue(value), language = 'en', required = true): string {
  const national = nationalPhoneDigits(value, country).replace(/^0+/, '')
  if (!national) return required ? (language === 'ar' ? 'يرجى إدخال رقم الهاتف المحمول.' : 'Please enter a mobile number.') : ''
  const name = language === 'ar' ? country.country_ar || country.country : country.country
  const invalid = language === 'ar'
    ? `أدخل رقم هاتف محمول صالحاً في ${name}: ${country.format}.`
    : `Enter a valid ${name} mobile: ${country.format}.`
  if (!new RegExp(country.prefix_pattern).test(national)) return invalid
  if (national.length < country.min_length) return language === 'ar' ? 'رقم الهاتف قصير جداً.' : 'Phone number is too short.'
  if (national.length > country.max_length) return language === 'ar' ? 'رقم الهاتف طويل جداً.' : 'Phone number is too long.'
  return new RegExp(country.pattern).test(national) ? '' : invalid
}
